import React from "react";
import LegalDocument from "./LegalDocument";

/* =========================================================================
 *  Apsession Terms of Service — verbatim from the signed PDF
 *  (Effective 1 September 2026).
 *
 *  Layout, table of contents, and cross-document linking come from
 *  <LegalDocument>; this file is content only.
 * ========================================================================= */

export const TERMS_EFFECTIVE_DATE = "September 1, 2026";
export const TERMS_LAST_UPDATED = "September 1, 2026";

const INTRO = [
    "These Terms of Service (“Terms”) govern your access to and use of the websites, applications, ticketing platform, event-management services, ticket-scanning services, Digital Ticket Outlet services, Verified Ticket Ambassador services, and other products and services provided by Apsession Limited (“Apsession,” “we,” “us,” or “our”).",
    "By creating an account, purchasing or receiving a ticket, creating or managing an event, accepting an Apsession invitation, selling tickets, scanning tickets, or otherwise using the Services, you agree to these Terms.",
    "If you do not agree to these Terms, you must not use the Services.",
];

const SECTIONS = [
    {
        id: "about-apsession",
        title: "1. About Apsession",
        blocks: [
            { p: "Apsession provides technology and services that allow event organizers to create and manage events and allow customers to discover events and purchase or receive tickets." },
            { p: "Apsession may also provide tools for:" },
            {
                list: [
                    "Online ticket sales",
                    "Cash ticket sales",
                    "Digital Ticket Outlets",
                    "Verified Ticket Ambassadors",
                    "Event managers",
                    "Gate Staff and ticket scanners",
                    "Complimentary and free tickets",
                    "Digital ticket delivery",
                    "Online and offline ticket scanning",
                    "Organizer payouts",
                    "Sales reporting and analytics",
                    "Event marketing",
                    "Event promotion and discovery",
                    "Other event-related services",
                ],
            },
            { p: "Unless expressly stated otherwise, Apsession is the ticketing platform and is not the producer, promoter, owner, venue operator, or organizer of events listed by independent Event Organizers.", strong: true },
        ],
    },
    {
        id: "eligibility",
        title: "2. Eligibility",
        blocks: [
            { p: "You must be legally capable of entering into a binding agreement to use the Services." },
            { p: "If you use Apsession on behalf of a business, organization, event, or other entity, you represent that you have authority to bind that entity to these Terms." },
        ],
    },
    {
        id: "accounts",
        title: "3. Accounts",
        blocks: [
            { p: "Certain Services require an Apsession account." },
            { p: "You agree to:" },
            {
                list: [
                    "Provide accurate information;",
                    "Keep your information current;",
                    "Protect your password and login credentials;",
                    "Restrict unauthorized access to your account; and",
                    "Notify Apsession promptly of suspected unauthorized activity.",
                ],
            },
            { p: "You are responsible for activity performed through your account or through persons to whom you have intentionally granted access, except where prohibited by applicable law." },
            { p: "Apsession may require additional authentication or identity verification where appropriate." },
        ],
    },
    {
        id: "events-and-organizers",
        title: "4. Events and Event Organizers",
        blocks: [
            { p: "Event Organizers are responsible for the events they create, publish, manage, or sell through Apsession." },
            { p: "Organizers are responsible for matters including:" },
            {
                list: [
                    "Event accuracy;",
                    "Event dates and times;",
                    "Venue information;",
                    "Ticket descriptions and pricing;",
                    "Event capacity;",
                    "Age restrictions;",
                    "Entry requirements;",
                    "Licences and permits;",
                    "Required insurance;",
                    "Entertainment and performers;",
                    "Security and staffing;",
                    "Event operations;",
                    "Compliance with applicable laws; and",
                    "Delivering the event substantially as advertised.",
                ],
            },
            { p: "Apsession may review events before or after publication but does not guarantee or endorse an event solely because it has been approved or listed." },
            { p: "Organizers using Apsession are also subject to the Apsession Organizer Agreement." },
        ],
    },
    {
        id: "event-approval",
        title: "5. Event Approval",
        blocks: [
            { p: "Apsession reserves the right to approve, reject, suspend, unpublish, or remove an event where reasonably necessary." },
            { p: "Reasons may include:" },
            {
                list: [
                    "Incomplete or misleading information;",
                    "Suspected fraud;",
                    "Prohibited activities;",
                    "Safety concerns;",
                    "Intellectual-property complaints;",
                    "Legal or regulatory concerns;",
                    "Excessive chargeback risk;",
                    "Previous Organizer violations;",
                    "Failure to provide requested verification; or",
                    "Violation of these Terms.",
                ],
            },
            { p: "Approval of an event does not constitute Apsession's endorsement or guarantee of that event." },
        ],
    },
    {
        id: "tickets",
        title: "6. Tickets",
        blocks: [
            { p: "Tickets purchased or issued through Apsession may contain a unique ticket number, link, barcode, QR code, or other validation credential." },
            { p: "Ticket credentials should be treated as confidential." },
            { p: "Users must not intentionally:" },
            {
                list: [
                    "Duplicate tickets;",
                    "Counterfeit tickets;",
                    "Alter ticket credentials;",
                    "Sell unauthorized copies;",
                    "Share ticket credentials in a manner that enables unauthorized use; or",
                    "Attempt to bypass Apsession ticket-validation systems.",
                ],
            },
            { p: "Where multiple copies of the same ticket credential are presented, Apsession and/or the Event Organizer may treat the first successfully validated ticket as the valid admission." },
        ],
    },
    {
        id: "digital-ticket-delivery",
        title: "7. Digital Ticket Delivery",
        blocks: [
            { p: "Apsession may deliver tickets through:" },
            {
                list: [
                    "Email;",
                    "SMS;",
                    "WhatsApp;",
                    "In-app delivery;",
                    "Ticket links; or",
                    "Other supported electronic methods.",
                ],
            },
            { p: "Purchasers are responsible for providing accurate contact information." },
            { p: "Apsession is not responsible for delivery failures caused by incorrect information supplied by the purchaser, carrier outages, spam filtering, blocked messages, unavailable third-party messaging services, or other circumstances outside Apsession's reasonable control." },
        ],
    },
    {
        id: "prices-and-fees",
        title: "8. Ticket Prices and Fees",
        blocks: [
            { p: "The price displayed during checkout may include:" },
            {
                list: [
                    "Ticket price;",
                    "Apsession service fees;",
                    "Payment-processing fees;",
                    "Taxes, where applicable; and",
                    "Other disclosed charges.",
                ],
            },
            { p: "Depending on the Organizer's pricing configuration, fees may be:" },
            {
                list: [
                    "Paid by the ticket buyer;",
                    "Absorbed by the Event Organizer;",
                    "Divided between the buyer and Organizer; or",
                    "Subject to a custom pricing arrangement.",
                ],
            },
            { p: "The total amount payable will be displayed before the transaction is completed." },
        ],
    },
    {
        id: "payments",
        title: "9. Payments",
        blocks: [
            { p: "Payments may be processed by Apsession or authorized third-party payment processors." },
            { p: "By submitting payment information, you authorize the applicable payment provider to process the amount displayed at checkout." },
            { p: "A transaction is not complete until successful payment authorization has been received." },
            { p: "Apsession may investigate, decline, cancel, reverse, void, or hold transactions where reasonably necessary because of:" },
            {
                list: [
                    "Fraud;",
                    "Unauthorized payment;",
                    "Processing errors;",
                    "Duplicate transactions;",
                    "Chargeback risk;",
                    "Security concerns; or",
                    "Violation of these Terms.",
                ],
            },
        ],
    },
    {
        id: "refunds",
        title: "10. Refunds",
        blocks: [
            { p: "Ticket refund eligibility may depend on:" },
            {
                list: [
                    "The Event Organizer's refund policy;",
                    "Event cancellation;",
                    "Event postponement;",
                    "Material event changes;",
                    "Applicable law;",
                    "Payment-provider rules; and",
                    "Apsession policies.",
                ],
            },
            { p: "Where permitted by law, tickets may be non-refundable unless the applicable event or Organizer policy states otherwise." },
            { p: "Event Organizers are generally financially responsible for refunds arising from cancellation of their events." },
            { p: "Apsession may process refunds on behalf of an Organizer." },
            { p: "Refund processing time may vary depending on the payment method, payment processor, card issuer, and financial institution." },
        ],
    },
    {
        id: "cancellations-and-changes",
        title: "11. Event Cancellations and Changes",
        blocks: [
            { p: "Event Organizers must promptly notify Apsession of cancellations, postponements, venue changes, significant time changes, or other material event modifications." },
            { p: "Apsession may communicate such changes to ticket holders." },
            { p: "Except where liability cannot legally be excluded, Apsession is not responsible for costs incurred by attendees in connection with an event, including:" },
            {
                list: [
                    "Transportation;",
                    "Accommodation;",
                    "Parking;",
                    "Meals;",
                    "Clothing;",
                    "Travel expenses; or",
                    "Other incidental expenses.",
                ],
            },
        ],
    },
    {
        id: "organizer-payouts",
        title: "12. Organizer Payouts",
        blocks: [
            { p: "Organizer payouts are governed primarily by the Apsession Organizer Agreement and any event-specific or custom pricing arrangement." },
            { p: "Apsession may delay, reduce, reserve, or withhold Organizer funds where reasonably necessary because of:" },
            {
                list: [
                    "Refund obligations;",
                    "Chargebacks;",
                    "Fraud investigations;",
                    "Event cancellation or postponement;",
                    "Event disputes;",
                    "Legal requirements;",
                    "Verification requirements;",
                    "Payment-processor requirements; or",
                    "Amounts owed to Apsession.",
                ],
            },
        ],
    },
    {
        id: "digital-ticket-outlets",
        title: "13. Digital Ticket Outlets",
        blocks: [
            { p: "Apsession may authorize selected businesses or individuals to operate as Digital Ticket Outlets." },
            { p: "Authorized outlets may sell digital event tickets and may collect payments in accordance with applicable Apsession procedures." },
            { p: "Outlets must:" },
            {
                list: [
                    "Accurately record sales;",
                    "Protect customer information;",
                    "Properly account for funds collected;",
                    "Remit amounts due according to applicable schedules;",
                    "Avoid unauthorized ticket sales; and",
                    "Comply with Apsession policies and applicable law.",
                ],
            },
            { p: "Failure to remit amounts due may result in suspension, termination, collection activity, or legal action." },
        ],
    },
    {
        id: "verified-ambassadors",
        title: "14. Verified Ticket Ambassadors",
        blocks: [
            { p: "Apsession may operate a Verified Ticket Ambassador program." },
            { p: "Eligible ambassadors may request authorization to sell tickets for events." },
            { p: "Verification by Apsession means an individual has satisfied Apsession's then-current verification requirements. It does not guarantee sales performance, conduct, financial reliability, or suitability for any specific Organizer." },
            { p: "Unless expressly agreed otherwise, ambassadors are independent participants and are not employees of Apsession." },
        ],
    },
    {
        id: "ambassador-commissions",
        title: "15. Ambassador Commissions",
        blocks: [
            { p: "Event Organizers may establish commissions for eligible Ticket Ambassadors." },
            { p: "Commission eligibility may be adjusted for:" },
            {
                list: [
                    "Refunded tickets;",
                    "Voided tickets;",
                    "Chargebacks;",
                    "Fraudulent transactions;",
                    "Cancelled events; and",
                    "Reversed transactions.",
                ],
            },
        ],
    },
    {
        id: "managers-sellers-gate-staff",
        title: "16. Managers, Sellers and Gate Staff",
        blocks: [
            { p: "Event Organizers may authorize managers, ticket sellers, Gate Staff, scanners, or other users to perform functions on their behalf." },
            { p: "The Organizer determines which permissions these users receive." },
            { p: "Apsession may offer permissions such as:" },
            {
                list: [
                    "Ticket scanning;",
                    "Gate ticket sales;",
                    "Event management;",
                    "Sales reporting; or",
                    "A combination of permissions.",
                ],
            },
            { p: "Organizers are responsible for controlling access and promptly removing permissions that are no longer appropriate." },
        ],
    },
    {
        id: "ticket-scanning",
        title: "17. Ticket Scanning",
        blocks: [
            { p: "Apsession provides tools designed to determine whether a ticket appears valid based on available Apsession records." },
            { p: "A successful scan does not independently verify the identity, age, conduct, or legal eligibility of the person presenting a ticket." },
            { p: "Event Organizers remain responsible for venue entry policies and event security." },
        ],
    },
    {
        id: "offline-scanning",
        title: "18. Offline Ticket Scanning",
        blocks: [
            { p: "Certain Apsession applications may allow ticket validation without an active internet connection." },
            { p: "Offline scanning depends on relevant event and ticket information being available on the device." },
            { p: "When multiple devices operate offline, real-time synchronization between devices may not be possible." },
            { p: "Organizers should synchronize devices whenever connectivity becomes available and follow Apsession's recommended procedures." },
        ],
    },
    {
        id: "complimentary-tickets",
        title: "19. Complimentary and Free Tickets",
        blocks: [
            { p: "Apsession may permit Organizers to distribute complimentary or free tickets." },
            { p: "Such tickets may still be subject to:" },
            {
                list: [
                    "Delivery costs;",
                    "Platform limits;",
                    "Upgrade requirements;",
                    "Validation requirements; or",
                    "Other disclosed conditions.",
                ],
            },
        ],
    },
    {
        id: "prohibited-conduct",
        title: "20. Prohibited Conduct",
        blocks: [
            { p: "Users must not use Apsession to:" },
            {
                list: [
                    "Commit fraud;",
                    "Sell counterfeit tickets;",
                    "Misrepresent events;",
                    "Process unauthorized payments;",
                    "Launder money;",
                    "Evade financial obligations;",
                    "Violate applicable law;",
                    "Infringe intellectual-property rights;",
                    "Harass or threaten others;",
                    "Introduce malicious software;",
                    "Attempt unauthorized system access;",
                    "Circumvent security systems;",
                    "Manipulate ticket sales or analytics;",
                    "Abuse refunds or chargebacks;",
                    "Create deceptive accounts; or",
                    "Use another person's identity without authorization.",
                ],
            },
        ],
    },
    {
        id: "event-and-user-content",
        title: "21. Event and User Content",
        blocks: [
            { p: "Users and Organizers may upload event names, descriptions, photographs, flyers, videos, logos, and other content." },
            { p: "Users retain ownership of content they own." },
            { p: "By providing content to Apsession, you grant Apsession a non-exclusive, worldwide, royalty-free licence to host, display, reproduce, resize, format, distribute, and use such content as reasonably necessary to:" },
            {
                list: [
                    "Operate the Services;",
                    "Display event listings;",
                    "Sell tickets;",
                    "Promote the applicable event; and",
                    "Promote Apsession and its Services.",
                ],
            },
            { p: "You represent that you have the necessary rights to provide such content." },
        ],
    },
    {
        id: "intellectual-property",
        title: "22. Intellectual Property",
        blocks: [
            { p: "Apsession's software, trademarks, logos, interface designs, technology, databases, graphics, and other proprietary materials are owned by or licensed to Apsession." },
            { p: "Except as expressly permitted, users may not copy, reproduce, modify, reverse engineer, distribute, sell, or exploit Apsession's intellectual property." },
        ],
    },
    {
        id: "fraud-prevention",
        title: "23. Fraud Prevention and Verification",
        blocks: [
            { p: "Apsession may use automated and manual systems to identify suspicious activity." },
            { p: "We may request additional information or identification before:" },
            {
                list: [
                    "Approving an event;",
                    "Processing certain transactions;",
                    "Activating selling capabilities;",
                    "Releasing a payout;",
                    "Approving a Ticket Ambassador;",
                    "Approving a Digital Ticket Outlet; or",
                    "Restoring a restricted account.",
                ],
            },
            { p: "Failure to provide reasonably requested verification may result in restricted access." },
        ],
    },
    {
        id: "suspension-and-termination",
        title: "24. Suspension and Termination",
        blocks: [
            { p: "Apsession may restrict, suspend, or terminate access to all or part of the Services where reasonably necessary to protect Apsession, users, purchasers, payment providers, or third parties." },
            { p: "Reasons may include:" },
            {
                list: [
                    "Violation of these Terms;",
                    "Fraud;",
                    "Unpaid balances;",
                    "Failure to remit funds;",
                    "Excessive chargebacks;",
                    "Security risks;",
                    "Illegal activity;",
                    "Misrepresentation; or",
                    "Abuse of Apsession Services.",
                ],
            },
        ],
    },
    {
        id: "third-party-services",
        title: "25. Third-Party Services",
        blocks: [
            { p: "Apsession may integrate with third-party payment processors, messaging providers, financial institutions, social-media services, analytics providers, cloud services, and other technology platforms." },
            { p: "Apsession is not responsible for the acts, omissions, outages, policies, or independent services of third parties outside Apsession's reasonable control." },
        ],
    },
    {
        id: "service-availability",
        title: "26. Service Availability",
        blocks: [
            { p: "Apsession aims to provide reliable Services but does not guarantee uninterrupted operation." },
            { p: "Services may occasionally become unavailable because of:" },
            {
                list: [
                    "Maintenance;",
                    "Internet outages;",
                    "Telecommunications failures;",
                    "Third-party service interruptions;",
                    "Payment-network outages;",
                    "Security incidents; or",
                    "Circumstances beyond Apsession's reasonable control.",
                ],
            },
        ],
    },
    {
        id: "disclaimer-events",
        title: "27. Disclaimer Regarding Events",
        blocks: [
            { p: "Unless expressly identified as an Organizer or co-organizer, Apsession does not control:" },
            {
                list: [
                    "Event performance;",
                    "Artist appearances;",
                    "Venue operations;",
                    "Security;",
                    "Food and beverages;",
                    "Event schedules;",
                    "Organizer conduct;",
                    "Weather conditions; or",
                    "Admission decisions.",
                ],
            },
            { p: "Nothing in these Terms excludes responsibility that Apsession cannot legally exclude." },
        ],
    },
    {
        id: "limitation-of-liability",
        title: "28. Limitation of Liability",
        blocks: [
            { p: "To the maximum extent permitted by applicable law, Apsession will not be liable for indirect, incidental, special, consequential, exemplary, or punitive loss arising from circumstances outside Apsession's reasonable control." },
            { p: "Nothing in these Terms excludes or limits liability where doing so is prohibited by applicable law." },
        ],
    },
    {
        id: "indemnification",
        title: "29. Indemnification",
        blocks: [
            { p: "To the extent permitted by law, commercial users and Event Organizers agree to indemnify and hold Apsession harmless from third-party claims, losses, liabilities, damages, and reasonable expenses resulting from:" },
            {
                list: [
                    "Their event;",
                    "Their content;",
                    "Their violation of law;",
                    "Their breach of these Terms;",
                    "Their infringement of third-party rights; or",
                    "Conduct of persons acting under their authority.",
                ],
            },
            { p: "Additional Organizer indemnification obligations are contained in the Apsession Organizer Agreement." },
        ],
    },
    {
        id: "privacy",
        title: "30. Privacy",
        blocks: [
            { p: "Use of Apsession is governed by the Apsession Privacy Policy." },
        ],
    },
    {
        id: "electronic-communications",
        title: "31. Electronic Communications",
        blocks: [
            { p: "By using Apsession, you consent to receiving electronic communications associated with your transactions, account, tickets, and use of the Services." },
            { p: "These may be delivered through:" },
            {
                list: [
                    "Email;",
                    "SMS;",
                    "WhatsApp;",
                    "Push notifications;",
                    "In-app messages; or",
                    "Other electronic channels.",
                ],
            },
        ],
    },
    {
        id: "changes-to-terms",
        title: "32. Changes to These Terms",
        blocks: [
            { p: "Apsession may update these Terms periodically." },
            { p: "Material changes may be communicated through the website, application, email, or other reasonable means." },
            { p: "The updated Terms will contain a revised “Last Updated” date." },
        ],
    },
    {
        id: "governing-law",
        title: "33. Governing Law",
        blocks: [
            { p: "These Terms are governed by and interpreted in accordance with the laws of Jamaica, without prejudice to mandatory consumer rights that may apply." },
        ],
    },
    {
        id: "disputes",
        title: "34. Disputes",
        blocks: [
            { p: "Users should first contact Apsession to attempt to resolve a dispute informally." },
            { p: "If a dispute cannot be resolved, either party may exercise remedies available under applicable Jamaican law before a court or other competent authority." },
        ],
    },
    {
        id: "severability",
        title: "35. Severability",
        blocks: [
            { p: "If any provision is determined to be invalid or unenforceable, the remaining provisions remain in effect to the extent permitted by law." },
        ],
    },
    {
        id: "no-waiver",
        title: "36. No Waiver",
        blocks: [
            { p: "Apsession's failure to enforce a provision does not constitute a waiver of that provision or any other right." },
        ],
    },
    {
        id: "entire-agreement",
        title: "37. Entire Agreement",
        blocks: [
            { p: "These Terms, together with:" },
            {
                list: [
                    "The Apsession Privacy Policy;",
                    "The Apsession Organizer Agreement where applicable;",
                    "Service-specific terms; and",
                    "Any specifically negotiated written agreement",
                ],
            },
            { p: "govern use of the applicable Apsession Services." },
        ],
    },
    {
        id: "contact",
        title: "38. Contact Apsession",
        blocks: [
            { contact: true },
        ],
    },
];

export default function TermsOfService() {
    return (
        <LegalDocument
            title="Terms of Service"
            path="/terms-of-service"
            effectiveDate={TERMS_EFFECTIVE_DATE}
            lastUpdated={TERMS_LAST_UPDATED}
            intro={INTRO}
            sections={SECTIONS}
        />
    );
}
